import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, ScrollView, TextInput, View } from 'react-native';

import { ActivityIndicator, Button, Modal, Text, useTheme } from '@/components/ui';
import { useUsers } from '@/hooks/use-data';
import { getUserTextColor } from '@/utils/user-colors';

export interface AssignUserModalProps {
	visible: boolean;
	currentUserId?: string | null;
	/** Who has it now, so the row can be ticked and "Unassign" can appear. */
	assignedTo?: string | null;
	busy?: boolean;
	onDismiss: () => void;
	onAssign: (userId: string | null) => void;
}

/**
 * Hand a message to a person — or take it back.
 *
 * Separate from the association and lot pickers on purpose: those answer what the message
 * is ABOUT, this answers who is dealing with it. A message can be perfectly filed to a
 * lot and still need someone's name on it, and it can be assigned long before anyone
 * works out which lot it belongs to.
 *
 * Filters the loaded user list in memory rather than per keystroke — an org is tens of
 * users, and a network round trip per character would blank the list mid-type for no
 * benefit (CLAUDE.md "Stable Search Inputs").
 */
export const AssignUserModal: React.FC<AssignUserModalProps> = ({
	visible,
	currentUserId,
	assignedTo,
	busy = false,
	onDismiss,
	onAssign,
}) => {
	const theme = useTheme();
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (visible) setQuery('');
	}, [visible]);

	// `limit: 500` because the filtering below is in memory: the default page is 50, and
	// contractors and homeowners share this table, so a colleague on page 2 would show
	// as "Nobody matches". Matches what the association picker does for the same reason.
	const { data: users, isLoading } = useUsers({ limit: 500 });

	const matches = useMemo(() => {
		const all = users?.data ?? [];
		const needle = query.trim().toLowerCase();
		const live = all.filter((u) => !u.archivedAt);
		const filtered = needle
			? live.filter((u) => `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(needle))
			: live;
		// Me first — self-assignment ("I'll take this") is the common case by a distance.
		return [...filtered].sort((a, b) => {
			if (a.id === currentUserId) return -1;
			if (b.id === currentUserId) return 1;
			return (a.name ?? '').localeCompare(b.name ?? '');
		}).slice(0, 60);
	}, [users, query, currentUserId]);

	return (
		<Modal visible={visible} onDismiss={onDismiss} testID="assign-user-modal">
			<View style={{ padding: 20, minWidth: 380, maxWidth: 560 }}>
				<Text style={{ fontSize: 18, fontWeight: '700', marginBottom: 4 }}>Assign to</Text>
				<Text style={{ color: theme.colors.onSurfaceVariant, marginBottom: 8 }}>
					Who is dealing with this message. Clear it again once it has been handled.
				</Text>

				<TextInput
					value={query}
					onChangeText={setQuery}
					placeholder="Filter people…"
					placeholderTextColor={theme.colors.onSurfaceVariant}
					testID="assign-user-search"
					style={{
						borderWidth: 1,
						borderColor: theme.colors.outline,
						borderRadius: 8,
						paddingHorizontal: 12,
						paddingVertical: 10,
						color: theme.colors.onSurface,
						marginBottom: 8,
					}}
				/>

				{isLoading ? (
					<View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator /></View>
				) : (
					<ScrollView style={{ maxHeight: 320 }} testID="assign-user-results">
						{matches.length === 0 ? (
							<Text style={{ color: theme.colors.onSurfaceVariant, paddingVertical: 16 }}>
								Nobody matches “{query}”.
							</Text>
						) : matches.map((user) => {
							const isCurrent = user.id === assignedTo;
							return (
								<Pressable
									key={user.id}
									onPress={() => onAssign(user.id)}
									disabled={busy}
									testID={`assign-user-${user.id}`}
									style={{
										flexDirection: 'row',
										alignItems: 'center',
										gap: 8,
										paddingVertical: 10,
										paddingHorizontal: 8,
										borderBottomWidth: 1,
										borderBottomColor: theme.colors.outlineVariant ?? theme.colors.outline,
										backgroundColor: isCurrent ? theme.colors.primaryContainer : 'transparent',
									}}
								>
									<Ionicons name="person-circle" size={18} color={getUserTextColor(user.id, user.color)} />
									<View style={{ flex: 1, minWidth: 0 }}>
										<Text numberOfLines={1} style={{ fontWeight: '600' }}>
											{user.name}{user.id === currentUserId ? ' (me)' : ''}
										</Text>
										<Text numberOfLines={1} style={{ fontSize: 12, color: theme.colors.onSurfaceVariant }}>
											{user.email}
										</Text>
									</View>
									{isCurrent ? <Ionicons name="checkmark-circle" size={18} color={theme.colors.primary} /> : null}
								</Pressable>
							);
						})}
					</ScrollView>
				)}

				<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
					{assignedTo ? (
						// The half of this feature that makes the other half useful.
						<Button mode="text" onPress={() => onAssign(null)} disabled={busy} testID="assign-user-clear">
							Unassign
						</Button>
					) : null}
					<Button mode="outlined" onPress={onDismiss} disabled={busy}>Cancel</Button>
				</View>
			</View>
		</Modal>
	);
};

export default AssignUserModal;
